/**
 * The capability-gated gym tabs, and what a viewer standing on one they cannot
 * see is shown.
 *
 * Utak (`routes.viewTab`), Építők (`stats.viewSetterTab` + `setterStats`) and
 * Admin (`adminPanel.access`) are in the tab bar only for a viewer who holds the
 * capability, but the URL can still name them: a `#admin` link opened by a
 * member, a bookmark, or a capability revoked while its tab is open (the member
 * document is live, so the tab leaves the bar at once). The tab's content
 * renders only behind the same check, so without a rule the viewer was left on
 * an empty page with no tab selected. The rule: once the viewer's capabilities
 * are known, a gated tab that is not in the bar goes back to the map — like the
 * Events/Competition tab without its features and the Spraywall tab without its
 * add-on.
 *
 * "Known" is the server-confirmed membership (`memberState` is "active" or
 * "none", never the optimistic first snapshot) AND the user document (a
 * platform admin's gym capabilities come from it, with no membership at all).
 * Before that the capability set is empty for everybody, and a gym admin
 * reloading `#admin/users` must not be thrown out of a tab they do hold.
 */

/** The tabs whose presence is decided by a capability. */
export const CAP_GATED_TABS = Object.freeze(["routes", "setters", "admin"]);

/**
 * @param {object}   p
 * @param {string}   p.tab            the tab the URL / state names
 * @param {string[]} p.visibleTabs    the keys of the tab bar the viewer sees
 * @param {"loading"|"active"|"none"} p.memberState  GymContext's server-confirmed membership
 * @param {boolean}  p.userLoaded     the viewer's user document has arrived
 * @returns {"stay"|"wait"|"bounce"}
 */
export function capGatedTabDecision({ tab, visibleTabs, memberState, userLoaded } = {}) {
  if (!CAP_GATED_TABS.includes(tab)) return "stay";
  if ((visibleTabs || []).includes(tab)) return "stay";
  if ((memberState !== "active" && memberState !== "none") || !userLoaded) return "wait";
  return "bounce";
}
